import { createFileRoute, Link } from "@tanstack/react-router";
import { PageHero } from "@/components/site/PageHero";
import { CTAButtons } from "@/components/site/CTAButtons";
import { CheckCircle2 } from "lucide-react";
import faceBgAssetUrl from "@/assets/izhub-face-bg.webp";
const faceBgAsset = { url: faceBgAssetUrl };

export const Route = createFileRoute("/pricing")({
  head: () => ({
    meta: [
      { title: "Cleaning Prices: Transparent Quotes | IZHUB Cleaning" },
      { name: "description", content: "Clear guide prices for domestic, deep, end of tenancy and specialist cleaning across Tidworth, Andover and Salisbury. Get a firm quote in 2 hours." },
      { property: "og:title", content: "Cleaning Prices: Transparent Quotes | IZHUB Cleaning" },
      { property: "og:description", content: "Clear guide prices for domestic, deep, end of tenancy and specialist cleaning across Salisbury Plain." },
      { property: "og:url", content: "https://izhubcleaning.uk/pricing" },
    ],
    links: [{ rel: "canonical", href: "https://izhubcleaning.uk/pricing" }],
  }),
  component: Pricing,
});

const tiers = [
  {
    name: "Domestic cleaning",
    from: "From £22/hour",
    desc: "Regular or one-off cleans for homes and apartments.",
    features: ["Weekly, fortnightly or one-off", "Consistent cleaner where possible", "Own equipment on request", "Insured for public liability"],
    highlight: false,
  },
  {
    name: "End of tenancy",
    from: "From £160",
    desc: "Inspection-ready cleaning for tenants, landlords and letting agents.",
    features: ["Whole-property clean to checkout standard", "Inside oven, cupboards and appliances", "Optional carpets & windows", "Re-clean support if issues raised"],
    highlight: true,
  },
  {
    name: "Commercial cleaning",
    from: "Custom quote",
    desc: "Small offices, workspaces and short-let properties.",
    features: ["Regular scheduled visits", "In-hours or out-of-hours", "Consumables restocked", "Contracted or ad-hoc"],
    highlight: false,
  },
  {
    name: "Military march-out",
    from: "Get a quote",
    desc: "Specialist cleaning to MOD inspection standard.",
    features: ["Cleaned to march-out standard", "Detailed oven, cupboards, fittings", "Pass-first-time approach", "Trusted by Tidworth & Bulford families"],
    highlight: false,
  },
];

function Pricing() {
  return (
    <>
      <PageHero
        title={<>Fair, transparent pricing; <span className="rainbow-text">no hidden fees</span>.</>}
        intro="Every property is different, so the final price depends on size, condition, type of clean, location and time required. Below are guide prices to help you plan. Get in touch for a firm, no-obligation quote within 2 hours."
        withBackground
        backgroundImage={faceBgAsset.url}
      >
        <CTAButtons />
      </PageHero>

      <section className="py-20 md:py-24">
        <div className="container-x grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {tiers.map((t) => (
            <div key={t.name} className={`rounded-3xl border p-6 flex flex-col ${t.highlight ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card"}`}>
              <h3 className={`font-display text-xl ${t.highlight ? "text-primary-foreground" : ""}`}>{t.name}</h3>
              <p className={`mt-2 font-display text-3xl ${t.highlight ? "text-sage-soft" : "text-primary"}`}>{t.from}</p>
              <p className={`mt-3 text-sm ${t.highlight ? "text-primary-foreground/85" : "text-muted-foreground"}`}>{t.desc}</p>
              <ul className="mt-6 space-y-2.5 flex-1">
                {t.features.map((f) => (
                  <li key={f} className="flex gap-2.5 text-sm">
                    <CheckCircle2 className={`h-4 w-4 mt-0.5 shrink-0 ${t.highlight ? "text-sage-soft" : "text-sage"}`} />
                    <span className={t.highlight ? "text-primary-foreground/90" : ""}>{f}</span>
                  </li>
                ))}
              </ul>
              <Link to="/quote" className={`mt-8 inline-flex items-center justify-center rounded-full px-5 py-2.5 text-sm font-medium transition-colors ${t.highlight ? "bg-background text-foreground hover:bg-sage-soft" : "bg-primary text-primary-foreground hover:opacity-90"}`}>
                Get a quote
              </Link>
            </div>
          ))}
        </div>
      </section>

      <section className="pb-20">
        <div className="container-x max-w-3xl">
          <div className="text-surface">
          <h2 className="font-display text-2xl md:text-3xl">What affects the final price?</h2>
          <ul className="mt-5 grid gap-2.5 sm:grid-cols-2 text-sm text-muted-foreground">
            <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 mt-0.5 text-sage shrink-0" />Property size (bedrooms &amp; bathrooms)</li>
            <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 mt-0.5 text-sage shrink-0" />Current condition of the property</li>
            <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 mt-0.5 text-sage shrink-0" />Type of clean required</li>
            <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 mt-0.5 text-sage shrink-0" />Add-ons (oven, carpets, windows inside)</li>
            <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 mt-0.5 text-sage shrink-0" />Location within our service area</li>
            <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 mt-0.5 text-sage shrink-0" />Frequency (regular vs one-off)</li>
          </ul>
          <p className="mt-8 text-sm text-muted-foreground">Prices shown are guide prices only and may change based on your specific requirements. We'll always confirm the final price before starting any work.</p>
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="container-x">
          <div className="rounded-3xl bg-cream/90 backdrop-blur-sm border border-border p-8 md:p-12 text-center">
            <h2 className="font-display text-3xl md:text-4xl">Get a firm quote in <span className="rainbow-text">under 2 hours</span>.</h2>
            <p className="mt-4 text-muted-foreground max-w-lg mx-auto">Tell us about your property and we'll come back with a clear, no-obligation price.</p>
            <div className="mt-8 flex justify-center"><CTAButtons /></div>
          </div>
        </div>
      </section>
    </>
  );
}
